import React, { useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';

// UTILS
import { useQuery } from '../../util/hooks';

// REDUX AND SLICES
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { changePassword } from '../../reducers/auth-slice';
import { fetchCheckoutAttributes } from '../../reducers/checkout-slice';

// WEB COMPONENTS
import { Button, IconLogo } from '@payright/web-components';

// OTHER COMPONENTS
import { ControlledInputField } from '../../components/form-fields';
import SCSpinner from '../../components/spinner';

// STYLES AND STYLED COMPONENTS
import SCEcommerceLogin from './css';
import { AppThunkDispatch } from '../../util/store';

interface LoginProps {}

type FormData = {
  tempPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ChangePassword = (props: LoginProps) => {
  const history = useHistory();
  const dispatch = useDispatch<AppThunkDispatch>();

  const location: any = useLocation();
  const user: string = location.state?.params || '';

  // URL QUERY PARAMS
  const query = useQuery();
  const queryParamCheckoutId = query.get('checkoutId');

  // GLOBAL STATE
  const globalAuthState = useSelector((state: RootState) => state.auth);

  // VARIABLES
  const { errorMessage, successMessage, loading } = globalAuthState;

  // START CHANGE PASSWORD FORM
  const reactHookForm = useForm<FormData>({
    defaultValues: {
      tempPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const { handleSubmit, formState, watch } = reactHookForm;

  const formSubmitHandler = (formData: FormData) => {
    dispatch(
      changePassword(user, formData.tempPassword, formData.newPassword, formData.confirmPassword)
    );
  };
  // END CHANGE PASSWORD FORM

  // LOAD LATEST CHECKOUT FROM THE SERVER
  useEffect(() => {
    if (queryParamCheckoutId) {
      dispatch(fetchCheckoutAttributes(queryParamCheckoutId));
    }
  }, [queryParamCheckoutId, dispatch]);

  return (
    <SCEcommerceLogin>
      <div className="ecommerce-image" />
      <div className="login-wrapper">
        <div className="login-header">
          <div className="logo">
            <IconLogo width={180} height={74} />
          </div>
          <h4>Change Password</h4>
        </div>
        <FormProvider {...reactHookForm}>
          <form onSubmit={handleSubmit(formSubmitHandler)} className="loginForm">
            <div data-testid="error-message">
              {errorMessage && <div className="validation-msg-box">{errorMessage}</div>}
            </div>
            <div data-testid="success-message">
              {successMessage && <div className="validation-msg-box-success">{successMessage}</div>}
            </div>
            <ControlledInputField
              placeholder="Please enter the temporary password"
              name="tempPassword"
              type="password"
              rules={{ required: true }}
            >
              Temporary Password
            </ControlledInputField>
            <ControlledInputField
              placeholder=""
              name="newPassword"
              type="password"
              rules={{ required: true }}
            >
              New Password
            </ControlledInputField>
            <ControlledInputField
              placeholder=""
              name="confirmPassword"
              type="password"
              rules={{
                required: 'Password is required',
                validate: (value: any) =>
                  value === watch('newPassword') || 'Password needs to match',
              }}
            >
              Confirm Password
            </ControlledInputField>
            <div className="forgot-password">
              <Link to={`/login/${history.location.search}`}>
                <span className="hide-mobile">Go back to login</span>
                <span className="mobile-only">Go back to login</span>
              </Link>
            </div>
            <Button
              withShadow
              disabled={formState.isValid === false || loading}
              maxWidth="100%"
              type="submit"
              iconPosition="right"
              icon={loading ? <SCSpinner /> : undefined}
            >
              Change Password
            </Button>
          </form>
        </FormProvider>
      </div>
    </SCEcommerceLogin>
  );
};

export default ChangePassword;
